import React, { useEffect, useMemo, useRef, useState } from 'react';
import './AdminNotificationsPage.scss';
import { DataTable } from '../../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import {
  createAdminNotification,
  deleteAdminNotification,
  getAdminNotifications,
  updateAdminNotification,
} from '../../../shared/services/admin/notifications/AdminNotificationsService';
import {
  AdminNotificationInput,
  AdminNotificationResponse,
} from '../../../shared/models/admin/notifications/AdminNotification';

const TARGET_OPTIONS = [
  { label: 'All accounts', value: 'ALL' },
  { label: 'Specific account', value: 'ACCOUNT' },
  { label: 'By account status', value: 'STATUS' },
];

const LEVEL_OPTIONS = [
  { label: 'Info', value: 'INFO' },
  { label: 'Warning', value: 'WARNING' },
  { label: 'Critical', value: 'CRITICAL' },
];

const STATUS_OPTIONS = ['PAID', 'TRIAL', 'OVERDUE', 'BLOCKED', 'DISABLED'].map((s) => ({
  label: s,
  value: s,
}));

interface FormState {
  titleEn: string;
  titlePl: string;
  descriptionEn: string;
  descriptionPl: string;
  level: string;
  priority: number;
  targetType: string;
  targetAccountId: number | null;
  targetStatus: string | null;
  active: boolean;
  startsAt: Date | null;
  endsAt: Date | null;
  ctaUrl: string;
  ctaLabelEn: string;
  ctaLabelPl: string;
}

const emptyForm: FormState = {
  titleEn: '',
  titlePl: '',
  descriptionEn: '',
  descriptionPl: '',
  level: 'INFO',
  priority: 0,
  targetType: 'ALL',
  targetAccountId: null,
  targetStatus: null,
  active: true,
  startsAt: null,
  endsAt: null,
  ctaUrl: '',
  ctaLabelEn: '',
  ctaLabelPl: '',
};

const AdminNotificationsPage: React.FC = () => {
  const [items, setItems] = useState<AdminNotificationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const startsRef = useRef<Calendar>(null);
  const endsRef = useRef<Calendar>(null);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getAdminNotifications());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (n: AdminNotificationResponse) => {
    const en = n.translations?.en;
    const pl = n.translations?.pl;
    setEditingId(n.id);
    setForm({
      titleEn: en?.title ?? '',
      titlePl: pl?.title ?? '',
      descriptionEn: en?.description ?? '',
      descriptionPl: pl?.description ?? '',
      level: n.level,
      priority: n.priority,
      targetType: n.targetType,
      targetAccountId: n.targetAccountId,
      targetStatus: n.targetStatus,
      active: n.active,
      startsAt: n.startsAt ? new Date(n.startsAt) : null,
      endsAt: n.endsAt ? new Date(n.endsAt) : null,
      ctaUrl: n.ctaUrl ?? '',
      ctaLabelEn: en?.ctaLabel ?? '',
      ctaLabelPl: pl?.ctaLabel ?? '',
    });
    setDialogOpen(true);
  };

  const isValid = useMemo(() => {
    if (!form.titleEn.trim() || !form.titlePl.trim()) return false;
    if (form.targetType === 'ACCOUNT' && form.targetAccountId == null) return false;
    if (form.targetType === 'STATUS' && !form.targetStatus) return false;
    return true;
  }, [form]);

  const toInput = (): AdminNotificationInput => ({
    translations: {
      en: {
        title: form.titleEn.trim(),
        description: form.descriptionEn.trim() || null,
        ctaLabel: form.ctaLabelEn.trim() || null,
      },
      pl: {
        title: form.titlePl.trim(),
        description: form.descriptionPl.trim() || null,
        ctaLabel: form.ctaLabelPl.trim() || null,
      },
    },
    level: form.level,
    priority: form.priority,
    targetType: form.targetType,
    targetAccountId: form.targetType === 'ACCOUNT' ? form.targetAccountId : null,
    targetStatus: form.targetType === 'STATUS' ? form.targetStatus : null,
    active: form.active,
    startsAt: form.startsAt ? form.startsAt.toISOString() : null,
    endsAt: form.endsAt ? form.endsAt.toISOString() : null,
    ctaUrl: form.ctaUrl.trim() || null,
  });

  const save = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      if (editingId != null) {
        await updateAdminNotification(editingId, toInput());
      } else {
        await createAdminNotification(toInput());
      }
      setDialogOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const remove = (n: AdminNotificationResponse) =>
    confirmDialog({
      message: `Delete notification "${
        n.translations?.en?.title ?? n.translations?.pl?.title ?? ''
      }"?`,
      header: 'Confirm',
      acceptClassName: 'btn btn--danger',
      accept: async () => {
        await deleteAdminNotification(n.id);
        await load();
      },
    });

  const targetLabel = (n: AdminNotificationResponse) => {
    if (n.targetType === 'ACCOUNT') return `Account #${n.targetAccountId}`;
    if (n.targetType === 'STATUS') return `Status: ${n.targetStatus}`;
    return 'All accounts';
  };

  return (
    <div className="admin-notifications">
      <div className="admin-notifications__toolbar">
        <Button className="btn btn--sm" label="Add notification" onClick={openCreate} />
      </div>

      <DataTable
        value={items}
        loading={loading}
        dataKey="id"
        tableClassName="table"
        className="table-wrapper table-wrapper--stack admin-notifications__table"
        responsiveLayout="stack"
        stripedRows
        removableSort
        emptyMessage="No notifications"
      >
        <Column
          header="Title"
          bodyClassName="table__cell--stack-title"
          body={(n: AdminNotificationResponse) =>
            n.translations?.en?.title ?? n.translations?.pl?.title ?? '—'
          }
        />
        <Column header="Target" body={targetLabel} bodyClassName="table__cell--stack-wide" />
        <Column field="level" header="Level" sortable style={{ width: '110px' }} />
        <Column field="priority" header="Priority" sortable style={{ width: '110px' }} />
        <Column
          header="Active"
          body={(n: AdminNotificationResponse) => (n.active ? 'Yes' : 'No')}
          style={{ width: '90px' }}
        />
        <Column
          header="Actions"
          bodyClassName="table__cell--stack-actions"
          style={{ width: '110px' }}
          body={(n: AdminNotificationResponse) => (
            <div className="admin-notifications__actions">
              <Button className="action-btn" onClick={() => openEdit(n)}>
                <span className="ico--edit ico--xs" />
              </Button>
              <Button className="action-btn" onClick={() => remove(n)}>
                <span className="ico--trash ico--xs" />
              </Button>
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header={editingId != null ? 'Edit notification' : 'New notification'}
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        className="dialog-box admin-notifications__dialog"
        draggable={false}
        resizable={false}
      >
        <div className="admin-notifications__form">
          <div className="admin-notifications__row">
            <label>
              <span>Title (EN) *</span>
              <InputText value={form.titleEn} onChange={(e) => set('titleEn', e.target.value)} />
            </label>
            <label>
              <span>Title (PL) *</span>
              <InputText value={form.titlePl} onChange={(e) => set('titlePl', e.target.value)} />
            </label>
          </div>

          <div className="admin-notifications__row">
            <label>
              <span>Description EN (opens a popup when set)</span>
              <InputTextarea
                value={form.descriptionEn}
                onChange={(e) => set('descriptionEn', e.target.value)}
                rows={4}
                autoResize
              />
            </label>
            <label>
              <span>Description PL</span>
              <InputTextarea
                value={form.descriptionPl}
                onChange={(e) => set('descriptionPl', e.target.value)}
                rows={4}
                autoResize
              />
            </label>
          </div>

          <div className="admin-notifications__row">
            <label>
              <span>Level</span>
              <Dropdown
                className="dropdown"
                value={form.level}
                options={LEVEL_OPTIONS}
                onChange={(e) => set('level', e.value)}
              />
            </label>
            <label>
              <span>Priority (higher first)</span>
              <InputNumber
                value={form.priority}
                onValueChange={(e) => set('priority', e.value ?? 0)}
                showButtons
                buttonLayout="stacked"
                incrementButtonIcon="ico ico--up ico--xs admin-notifications__chevron-up"
                decrementButtonIcon="ico ico--up ico--xs"
                pt={{ root: { className: 'admin-notifications__number' } }}
              />
            </label>
          </div>

          <div className="admin-notifications__row">
            <label>
              <span>Target</span>
              <Dropdown
                className="dropdown"
                value={form.targetType}
                options={TARGET_OPTIONS}
                onChange={(e) => set('targetType', e.value)}
              />
            </label>
            {form.targetType === 'ACCOUNT' && (
              <label>
                <span>Account ID *</span>
                <InputNumber
                  value={form.targetAccountId}
                  onValueChange={(e) => set('targetAccountId', e.value ?? null)}
                  useGrouping={false}
                />
              </label>
            )}
            {form.targetType === 'STATUS' && (
              <label>
                <span>Account status *</span>
                <Dropdown
                  className="dropdown"
                  value={form.targetStatus}
                  options={STATUS_OPTIONS}
                  onChange={(e) => set('targetStatus', e.value)}
                  placeholder="Select status"
                />
              </label>
            )}
          </div>

          <div className="admin-notifications__row">
            <label>
              <span>Starts at (optional)</span>
              <Calendar
                ref={startsRef}
                value={form.startsAt}
                onChange={(e) => set('startsAt', (e.value as Date) ?? null)}
                onShow={() => endsRef.current?.hide()}
                showTime
                showButtonBar
                dateFormat="yy-mm-dd"
                todayButtonClassName="btn btn--secondary btn--sm"
                clearButtonClassName="btn btn--primary btn--sm"
                pt={{ panel: { className: 'calendar-panel' } }}
              />
            </label>
            <label>
              <span>Ends at (optional)</span>
              <Calendar
                ref={endsRef}
                value={form.endsAt}
                onChange={(e) => set('endsAt', (e.value as Date) ?? null)}
                onShow={() => startsRef.current?.hide()}
                showTime
                showButtonBar
                dateFormat="yy-mm-dd"
                todayButtonClassName="btn btn--secondary btn--sm"
                clearButtonClassName="btn btn--primary btn--sm"
                pt={{ panel: { className: 'calendar-panel' } }}
              />
            </label>
          </div>

          <label>
            <span>CTA URL (optional)</span>
            <InputText value={form.ctaUrl} onChange={(e) => set('ctaUrl', e.target.value)} />
          </label>

          <div className="admin-notifications__row">
            <label>
              <span>CTA label EN (optional)</span>
              <InputText
                value={form.ctaLabelEn}
                onChange={(e) => set('ctaLabelEn', e.target.value)}
              />
            </label>
            <label>
              <span>CTA label PL (optional)</span>
              <InputText
                value={form.ctaLabelPl}
                onChange={(e) => set('ctaLabelPl', e.target.value)}
              />
            </label>
          </div>

          <label className="admin-notifications__checkbox">
            <Checkbox checked={form.active} onChange={(e) => set('active', !!e.checked)} />
            <span>Active</span>
          </label>

          <div className="admin-notifications__dialog-actions">
            <Button className="btn btn--secondary" label="Cancel" onClick={() => setDialogOpen(false)} />
            <Button className="btn" label="Save" onClick={save} disabled={!isValid || saving} />
          </div>
        </div>
      </Dialog>

      <ConfirmDialog className="dialog-box dialog-box--accept" />
    </div>
  );
};

export default AdminNotificationsPage;
